import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CommissionTable from './components/CommissionTable';
import UserSummaryPanel from './components/UserSummaryPanel';
import {
    listCommissions,
    approveCommission,
    cancelCommission,
    restoreCommission,
    approveAllPending,
} from './api';
import './commissions.css';

const FILTERS = [
    { value: '', label: 'همه' },
    { value: 'pending', label: 'در انتظار' },
    { value: 'paid', label: 'پرداخت شده' },
    { value: 'cancelled', label: 'لغو شده' },
];

export default function CommissionsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const beneficiaryId = searchParams.get('beneficiary_id') || '';

    const [commissions, setCommissions] = useState([]);
    const [filter, setFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await listCommissions({
                statusFilter: filter,
                beneficiaryId: beneficiaryId || undefined,
            });
            setCommissions(data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, beneficiaryId]);

    const handleApprove = async (id) => {
        setLoading(true);
        setError('');
        setMessage('');
        try {
            await approveCommission(id);
            setMessage('پورسانت با موفقیت تأیید و پرداخت شد.');
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (id) => {
        if (
            !window.confirm(
                'این پورسانت لغو شود؟ اگر قبلاً پرداخت شده باشد، مبلغ از کیف پول کاربر کسر می‌شود.'
            )
        ) {
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');
        try {
            await cancelCommission(id);
            setMessage('پورسانت لغو شد.');
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async (id) => {
        if (
            !window.confirm(
                'این پورسانت به حالت «در انتظار» بازگردانده شود؟ اگر قبلاً مبلغ از کیف پول کسر شده، دوباره به کیف پول اضافه می‌شود.'
            )
        ) {
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');
        try {
            await restoreCommission(id);
            setMessage('پورسانت با موفقیت به حالت در انتظار برگشت.');
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleApproveAll = async () => {
        if (!window.confirm('همه‌ی پورسانت‌های در انتظار تأیید شوند؟')) return;

        setLoading(true);
        setError('');
        setMessage('');
        try {
            const result = await approveAllPending();
            setMessage(
                `${result.approved} پورسانت تأیید شد${
                    result.failed ? `، ${result.failed} ناموفق` : ''
                }.`
            );
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const clearBeneficiaryFilter = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('beneficiary_id');
        setSearchParams(newParams);
    };

    return (
        <div className="commissions-page">
            <header className="page-header">
                <div>
                    <h1>مدیریت پورسانت‌ها</h1>
                    <p>لیست پورسانت‌های تولید شده از زنجیره معرفی</p>
                </div>
                <button
                    className="btn-approve-all"
                    onClick={handleApproveAll}
                    disabled={loading}
                >
                    تأیید همه‌ی در انتظار
                </button>
            </header>

            {beneficiaryId && (
                <div className="filter-notice">
                    <span>در حال نمایش پورسانت‌های یک کاربر خاص</span>
                    <button onClick={clearBeneficiaryFilter}>حذف فیلتر</button>
                </div>
            )}

            <UserSummaryPanel userId={beneficiaryId} />

            <div className="filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.value}
                        className={`filter-btn ${filter === f.value ? 'active' : ''}`}
                        onClick={() => setFilter(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {message && <div className="success-banner">{message}</div>}
            {error && <div className="error-banner">{error}</div>}

            <CommissionTable
                commissions={commissions}
                onApprove={handleApprove}
                onCancel={handleCancel}
                onRestore={handleRestore}
                loading={loading}
                showUserLinks
            />
        </div>
    );
}